import { type DragEvent, type FC, type ReactNode } from "react";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import PopupManager from "../../Popups/PopupManager";
import { MEDIA_URL } from "./MediaUtilities";

interface Props {
    visible: boolean;
    onVisible: (value: boolean) => void;
    dragging: boolean;
    onDragging: (value: boolean) => void;
    uploading: boolean;
    onUploading: (value: boolean) => void;
    canUpload: boolean;
    onUpload: (upload: FileList | string) => Promise<void>;
}

export const MediaSelectorOverlay: FC<Props> = ({
    visible,
    onVisible,
    dragging,
    onDragging,
    uploading,
    canUpload,
    onUpload,
}) => {
    const classes = [ "upload-overlay" ];

    let content: ReactNode = null;

    if(!canUpload && visible) {
        classes.push("visible");

        if(dragging) {
            classes.push("dragging");
        }

        content = (
            <div>
                <span className="icon">
                    <FontAwesomeLight icon="lock-alt" />
                </span>
                <p>You do not have permission to upload files to the media center.</p>
            </div>
        );
    } else if(uploading) {
        classes.push("visible");
        classes.push("uploading");

        content = (
            <div>
                <span className={"icon"}><FontAwesomeLight icon="spinner" className="fa-pulse" /></span>
                <p>Uploading...</p>
            </div>
        );
    } else if(visible) {
        classes.push("visible");

        if(dragging) {
            classes.push("dragging");
            content = (
                <div>
                    <span className={"icon"}><FontAwesomeLight icon={"upload"} /></span>
                    <p>Drop the files here to upload</p>
                </div>
            );
        } else {
            content = (
                <div>
                    <span className={"icon"}><FontAwesomeLight icon={"upload"} /></span>
                    <p>Drag and drop your file here</p>
                    <p>or click to browse your local computer for files</p>
                </div>
            );
        }
    }

    const onDragLeave = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        onVisible(false);
        onDragging(false);
    };

    const catchDragOver = (e: DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onDrop = async (e: DragEvent<HTMLDivElement>): Promise<void> => {
        e.preventDefault();
        e.stopPropagation();

        onVisible(false);
        onDragging(false);

        if(!canUpload) {
            PopupManager.warning("You do not have permission to do this");

            return;
        }

        let upload: FileList | string = e.dataTransfer.files;

        if(!(upload && upload.length)) {
            const url = e.dataTransfer.getData("url");

            if(url && !url.includes(MEDIA_URL)) {
                upload = url;
            } else {
                return;
            }
        }

        return onUpload(upload);
    };

    return (
        <div
            className={classes.join(" ")}
            onDrop={onDrop}
            onDragLeave={onDragLeave}
            onDragOver={catchDragOver}
        >
            {content}
        </div>
    );
};
